import {
  AttachmentOutlinedIcon,
  EllipsisText,
  FileDownloadOutlinedIcon,
  Flex,
  styled,
  VisibilityOutlinedIcon,
} from '@shared/components';
import { useDisclosure } from '@shared/hooks';
import { randomUUID } from '@shared/utils';
import { useCallback, useState } from 'react';

import { PreviewImageModal } from '../preview-image-modal';

const Wrapper = styled(Flex)(({ theme }) => ({
  width: '100%',
  gap: theme.spacing(2),
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

const AttachedFileWrapper = styled(Wrapper)(({ theme }) => ({
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: 'max-content',
  gap: theme.spacing(2),
}));

const FileItemWrapper = styled(Flex)(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.palette.grey[50],
  padding: theme.spacing(2.5, 3.5),
  borderRadius: theme.shape.rounded(1),
  gap: theme.spacing(2),
  width: '100%',
}));

const EndAdornmentWrapper = styled(Flex)(({ theme }) => ({
  marginLeft: 'auto',
  gap: theme.spacing(4),
}));

interface AttachedFile {
  name: string;
  value:
    | File
    | {
        url: string;
        name: string;
      };
  mode?: 'preview' | 'download';
  endAdornment?: React.ReactNode;
}

export interface AttachedFilePreviewProps {
  files: AttachedFile[];
  startIcon?: React.ReactNode;
}

export default function AttachedFilePreview(props: Readonly<AttachedFilePreviewProps>) {
  const { files, startIcon } = props;

  const [isImagePreviewerModalOpen, imagePreviewerModalHandlers] = useDisclosure();
  const { open: openImagePreviewerModal, close: closeImagePreviewerModal } =
    imagePreviewerModalHandlers;
  const [previewImageUrl, setPreviewImageUrl] = useState<string | null>(null);

  const createDownloadLink = useCallback((href: string, filename: string) => {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(href);
  }, []);

  const handleDownload = useCallback(
    (file: AttachedFile['value']) => {
      if (file instanceof File) {
        const objectUrl = URL.createObjectURL(file);
        createDownloadLink(objectUrl, file.name);
        return;
      }
      createDownloadLink(file.url, file.name);
    },
    [createDownloadLink],
  );

  const handlePreview = useCallback(
    (file: AttachedFile['value']) => {
      const url = file instanceof File ? URL.createObjectURL(file) : file.url;
      setPreviewImageUrl(url);
      openImagePreviewerModal();
    },
    [openImagePreviewerModal],
  );

  const handleClosePreview = useCallback(() => {
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl(null);
    }
    closeImagePreviewerModal();
  }, [previewImageUrl, closeImagePreviewerModal]);

  return (
    <>
      <AttachedFileWrapper>
        {files.map((file) => (
          <FileItemWrapper key={randomUUID()}>
            {startIcon ? (
              startIcon
            ) : (
              <AttachmentOutlinedIcon
                sx={(theme) => ({ color: theme.palette.text.secondary, ml: theme.spacing(0.5) })}
              />
            )}
            <EllipsisText sx={(theme) => ({ color: theme.palette.text.secondary })}>
              {file.name}
            </EllipsisText>
            <EndAdornmentWrapper>
              {file.mode === 'download' ? (
                <FileDownloadOutlinedIcon
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handleDownload(file.value)}
                />
              ) : file.mode === 'preview' ? (
                <VisibilityOutlinedIcon
                  sx={{ cursor: 'pointer' }}
                  onClick={() => handlePreview(file.value)}
                />
              ) : null}
              {file.endAdornment}
            </EndAdornmentWrapper>
          </FileItemWrapper>
        ))}
      </AttachedFileWrapper>
      {previewImageUrl ? (
        <PreviewImageModal
          open={isImagePreviewerModalOpen}
          image={previewImageUrl}
          onClose={handleClosePreview}
        />
      ) : null}
    </>
  );
}
